import { Controller } from "react-hook-form";

import { Dropdown } from "@alpacahq/ui";

import { Icon } from "~/v2/components/AssetIcon/AssetIcon";
import { FloatingButtons } from "~/v2/components/common/FloatingButtons";
import { InputField } from "~/v2/components/common/InputField";
import type { AssetAddressData, AssetAddressDisplay } from "../types";
import { BENEFICIARY_TYPE_OPTIONS, type BeneficiaryType } from "../types";
import { AddressMatchModal } from "./presentation/AddressMatchModal";
import { BasicInformation } from "./presentation/BasicInformation";
import { RequestedFieldsPanel } from "./presentation/RequestedFieldsPanel";
import { YesNoToggle } from "./presentation/YesNoToggle";
import type { UseTravelRuleFormReturn } from "./TravelRuleForm.controller";
import { VaspSearchDropdown } from "./VaspSearch";

interface TravelRuleFormPresentationProps extends UseTravelRuleFormReturn {
  onBack?: () => void;
  assetAddressData: AssetAddressData;
  assetAddressDisplay: AssetAddressDisplay;
}

export const TravelRuleFormPresentation = ({
  control,
  errors,
  beneficiaryType,
  beneficiaryTypeError,
  confirmedAuthorizedError,
  requestedFieldsError,
  isSelfHosted,
  ownsWallet,
  needsVasp,
  countries,
  vaspSearch,
  onBeneficiaryTypeChange,
  onIsSelfHostedChange,
  onOwnsWalletChange,
  onSubmit,
  isSubmitLoading,
  isSubmitDisabled,
  request,
  panelClaimedPaths,
  selectedBranchId,
  onSelectBranch,
  mode,
  confirmedAuthorized,
  onConfirmedAuthorizedChange,
  onBack,
  assetAddressData,
  assetAddressDisplay,
  addressMatchModal,
  onAcceptAddressMatch,
  onCancelAddressMatch,
  walletOwnerName,
}: TravelRuleFormPresentationProps) => {
  // RequestedFieldsPanel is the single source of truth for any wire path it
  // claims; suppress the equivalent main-form input so the user only sees one
  // input per key. UI-only controls (beneficiary type, ownsWallet) stay unconditional.
  const claimsAnyName =
    panelClaimedPaths.has("beneficiary_given_name") ||
    panelClaimedPaths.has("beneficiary_family_name");
  const claimsEntityName = panelClaimedPaths.has("beneficiary_entity_name");
  const claimsVasp =
    panelClaimedPaths.has("beneficiary_vasp_id") ||
    panelClaimedPaths.has("beneficiary_manual_entry.vasp_name") ||
    panelClaimedPaths.has("beneficiary_manual_entry.vasp_website");
  const claimsSelfHosted = panelClaimedPaths.has("beneficiary_is_self_hosted");

  return (
    <>
      <div className="flex flex-col gap-4">
        <BasicInformation
          chain={{
            label: assetAddressDisplay.chainLabel,
            icon: <Icon name={assetAddressDisplay.chainCode} />,
          }}
          asset={{
            label: assetAddressDisplay.assetLabel,
            icon: <Icon name={assetAddressDisplay.assetCode} />,
          }}
          address={assetAddressData.address}
        />

        <form onSubmit={onSubmit} className="flex h-fit flex-col gap-6 pb-60">
          <div className="rounded-lg border border-gray-300 bg-white p-4">
            <div className="flex flex-col gap-5">
              <div className="relative isolate z-50">
                <Dropdown
                  label="Who are you sending funds to?"
                  options={BENEFICIARY_TYPE_OPTIONS}
                  value={beneficiaryType ?? undefined}
                  onChange={(val) =>
                    onBeneficiaryTypeChange(val as BeneficiaryType)
                  }
                  placeholder="Select..."
                  error={beneficiaryTypeError}
                  fullWidth
                />
              </div>

              {!claimsSelfHosted && (
                <div className="relative isolate z-0">
                  <YesNoToggle
                    label="Is this a self-hosted wallet?"
                    value={isSelfHosted}
                    onChange={onIsSelfHostedChange}
                    errorMsg={errors.beneficiary_is_self_hosted?.message}
                  />
                </div>
              )}

              <div className="relative isolate z-0">
                <YesNoToggle
                  label="Do you own this wallet?"
                  value={ownsWallet}
                  onChange={onOwnsWalletChange}
                />
              </div>
            </div>

            {beneficiaryType === "individual" && !claimsAnyName && (
              <div className="flex flex-col gap-3 pt-5">
                <div className="grid grid-cols-2 gap-2">
                  <Controller
                    name="beneficiary_given_name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <InputField
                        label="First Name"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        error={error?.message}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="beneficiary_family_name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <InputField
                        label="Last Name"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        error={error?.message}
                        fullWidth
                      />
                    )}
                  />
                </div>
              </div>
            )}

            {beneficiaryType === "business" && !claimsEntityName && (
              <div className="pt-5">
                <Controller
                  name="beneficiary_entity_name"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <InputField
                      label="Legal Organization Name"
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      error={error?.message}
                      fullWidth
                    />
                  )}
                />
              </div>
            )}

            {needsVasp && !claimsVasp && (
              <div className="pt-5">
                <VaspSearchDropdown
                  vaspSearch={vaspSearch}
                  vaspSelectionError={errors.beneficiary_vasp_id?.message}
                  manualVaspNameError={
                    errors.beneficiary_manual_entry?.vasp_name?.message
                  }
                  manualVaspWebsiteError={
                    errors.beneficiary_manual_entry?.vasp_website?.message
                  }
                />
              </div>
            )}

            {request && (
              <>
                <RequestedFieldsPanel
                  request={request}
                  control={control}
                  countries={countries}
                  selectedBranchId={selectedBranchId}
                  onSelectBranch={onSelectBranch}
                />
                {requestedFieldsError && (
                  <p className="m-0 mt-1 p-0 text-red-500 text-xs">
                    {requestedFieldsError}
                  </p>
                )}
              </>
            )}
          </div>

          <FloatingButtons
            fixedOnBottom
            className="bg-white"
            topContent={
              <div className="flex flex-col gap-1">
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    checked={confirmedAuthorized}
                    onChange={(e) =>
                      onConfirmedAuthorizedChange(e.target.checked)
                    }
                    className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-gray-400 text-indigo-600 focus:ring-indigo-600"
                  />
                  <span className="select-none text-gray-800 text-sm leading-5">
                    I confirm that the information provided is accurate and that
                    I am authorized to interact with this address.
                  </span>
                </label>
                {confirmedAuthorizedError && (
                  <p className="m-0 p-0 text-red-500 text-xs">
                    {confirmedAuthorizedError}
                  </p>
                )}
              </div>
            }
            next={{
              disabled: isSubmitDisabled,
              loading: isSubmitLoading,
              label: mode.kind === "edit" ? "Update" : "Add Address",
              type: "submit",
            }}
            back={onBack ? { onClick: onBack, label: "Back" } : undefined}
          />
        </form>
      </div>

      <AddressMatchModal
        isOpen={addressMatchModal !== null}
        onClose={onCancelAddressMatch}
        onConfirm={onAcceptAddressMatch}
        isConfirmLoading={isSubmitLoading}
        chainLabel={assetAddressDisplay.chainLabel}
        chainCode={assetAddressDisplay.chainCode}
        assetLabel={assetAddressDisplay.assetLabel}
        assetCode={assetAddressDisplay.assetCode}
        address={assetAddressData.address}
        sendingTo={
          beneficiaryType === "business"
            ? "Business / Organization"
            : "Individual"
        }
        isSelfHosted={false}
        ownsWallet={ownsWallet}
        walletOwnerName={walletOwnerName}
        vaspName={addressMatchModal?.vaspName ?? ""}
        vaspDid={addressMatchModal?.vaspDid ?? ""}
      />
    </>
  );
};
